
import Vue from 'vue';
import Component from 'vue-class-component';
import {Prop} from 'vue-property-decorator';
import {IWhatsappMessage} from '@/model/Helpdesk/IWhatsapp';
import {saveMedia} from '@/services/helpdeskWhatsappMedia';
import {nameFromEmail} from '@/components/helpdesk/HelpdeskAssignee.vue';

const STATUS_ICONS: {[status: string]: string} = {
  queued: 'mdi-clock-outline',
  sent: 'mdi-check',
  delivered: 'mdi-check-all',
  read: 'mdi-check-all',
  failed: 'mdi-alert-circle-outline',
};

// One chat message: text, media, location or template, with Meta's delivery ticks for ours.
@Component({name: 'WhatsappMessageBubble'})
export default class WhatsappMessageBubble extends Vue {
  @Prop({required: true}) message!: IWhatsappMessage;

  downloading = false;
  mediaError = '';

  get mime(): string {
    return this.message.media ? this.message.media.mime || '' : '';
  }

  get isImage(): boolean {
    return this.message.type === 'image' || this.message.type === 'sticker' || this.mime.indexOf('image/') === 0;
  }

  get isAudio(): boolean {
    return this.message.type === 'audio' || this.mime.indexOf('audio/') === 0;
  }

  get isVideo(): boolean {
    return this.message.type === 'video' || this.mime.indexOf('video/') === 0;
  }

  get fileIcon(): string {
    if (this.isImage) return 'mdi-file-image-outline';
    if (this.isAudio) return 'mdi-file-music-outline';
    if (this.isVideo) return 'mdi-file-video-outline';
    if (this.mime === 'application/pdf') return 'mdi-file-pdf-box';
    return 'mdi-file-document-outline';
  }

  get fileName(): string {
    if (this.message.media && this.message.media.filename) return this.message.media.filename;
    if (this.isImage) return this.$t('helpdesk.whatsapp.message.image') as string;
    if (this.isAudio) return this.$t('helpdesk.whatsapp.message.audio') as string;
    if (this.isVideo) return this.$t('helpdesk.whatsapp.message.video') as string;
    return this.$t('helpdesk.whatsapp.message.document') as string;
  }

  get authorName(): string {
    return this.message.sent_by_name || nameFromEmail(this.message.sent_by_email || '');
  }

  get time(): string {
    return new Date(this.message.created_at).toLocaleTimeString('es-ES', {hour: '2-digit', minute: '2-digit'});
  }

  get statusIcon(): string {
    return STATUS_ICONS[this.message.status] || 'mdi-check';
  }

  get statusLabel(): string {
    return this.$t('helpdesk.whatsapp.messageStatus.' + this.message.status) as string;
  }

  get mapsUrl(): string {
    const l = this.message.location;
    return l ? 'https://www.google.com/maps?q=' + l.latitude + ',' + l.longitude : '';
  }

  download() {
    if (!this.message.media || !this.message.media.url || this.downloading) return;
    this.downloading = true;
    this.mediaError = '';
    saveMedia(this.message.media, 'whatsapp-' + this.message.id)
      .catch((error: {status: number, message: string | null}) => {
        this.mediaError = error.status === 404
          ? this.$t('helpdesk.whatsapp.message.mediaGone') as string
          : error.message || this.$t('helpdesk.whatsapp.message.mediaError') as string;
      })
      .then(() => { this.downloading = false; });
  }
}
